import { z } from "incur";
import { createWhopFetch, resolveActiveAccountId } from "./api";
import { responseError } from "./lib/http";

const feedbackOptions = z.object({
	account_id: z
		.string()
		.regex(/^biz_[A-Za-z0-9]+$/)
		.max(255)
		.optional()
		.describe(
			"Account ID (biz_...) for the affected resource. Defaults to your active account when available.",
		),
	content: z
		.string()
		.trim()
		.min(1)
		.max(12000)
		.describe(
			"Describe the problem or unanswered question and relevant context. Remove credentials, personal data, and payment details.",
		),
});

const receipt = z.object({ id: z.string().startsWith("fbk_") });
const whopFetch = createWhopFetch();

export function buildFeedbackCommand(kind: "report_feedback" | "ask_question") {
	return {
		description:
			kind === "ask_question"
				? "Record an unanswered Whop question for internal review. Returns a receipt, not an answer; do not wait or poll for a reply."
				: "Report a concrete Whop problem, confusing behavior, or documentation gap for internal review. Returns a receipt; no follow-up is sent to this session.",
		hint: "Use a user credential from `whop login` (browser OAuth). Submit one real observation per call.",
		options: feedbackOptions,
		output: z.object({
			submission_id: z.string(),
			status: z.literal("submitted"),
			message: z.string(),
		}),
		async run({ options }: { options: z.infer<typeof feedbackOptions> }) {
			const content = options.content
				.replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/g, "Bearer [redacted]")
				.replace(/\b(?:apik|sk|whsec|rt|at)_[A-Za-z0-9]{8,}\b/g, "[redacted]");
			const response = await whopFetch(
				new Request("https://whop.invalid/feedback_submissions", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"Idempotency-Key": crypto.randomUUID(),
					},
					body: JSON.stringify({
						source: `cli_${kind}`,
						content,
						account_id: options.account_id ?? (await resolveActiveAccountId()),
					}),
				}),
			);
			if (!response.ok) throw await responseError(response);
			const saved = receipt.safeParse(await response.json());
			if (!saved.success) throw new Error("Invalid feedback receipt");
			return {
				submission_id: saved.data.id,
				status: "submitted" as const,
				message:
					kind === "ask_question"
						? "Question accepted for Whop's internal review. No answer will be returned in this session; continue with available information."
						: "Feedback accepted for Whop's internal review. No follow-up will be returned in this session.",
			};
		},
	};
}
